<div class="bulk-shell" *ngIf="(isAdmin || isManager) && canBulkSettingsUpdate()">
  <div class="bulk-steps">
    <!-- Step 1 -->
    <section class="bulk-step">
      <header class="bulk-step-header">
        <div class="bulk-step-title">
          <span class="bulk-step-num" aria-hidden="true">1</span>
          <div>
            <h3>Select {{ entityType === 'employee' ? 'employees' : 'teams' }}</h3>
            <p>Who should receive these settings</p>
          </div>
        </div>
        <div class="bulk-header-actions">
          <div class="bulk-segment" role="tablist" aria-label="Update target">
            <button type="button" role="tab" (click)="switchEntityType('employee')"
              [attr.aria-selected]="entityType === 'employee'"
              [class.active]="entityType === 'employee'">
              Employees
            </button>
            <button type="button" role="tab" (click)="switchEntityType('team')"
              [attr.aria-selected]="entityType === 'team'"
              [class.active]="entityType === 'team'">
              Teams
            </button>
          </div>
          <span class="bulk-count">{{ getSelectedCount() }} of {{ getTotalCount() }}</span>
        </div>
      </header>

      <div class="bulk-search-row">
        <div class="bulk-search-wrap">
          <app-employee-search [userList]="allEmployeeList"
            [placeholder]="entityType === 'employee' ? 'Search people' : 'Search teams'"
            [isNeedUserReturn]="true" class="bulk-search" [selectedEmployeeId]="''"
            (searchEmployeeName)="onSearchChange($event)" [isInputSearch]="true">
          </app-employee-search>
        </div>
        <mat-form-field class="timeline-datepicker bulk-team-field" *ngIf="entityType === 'team'"
          appearance="outline">
          <mat-select placeholder="Filter by team" [(ngModel)]="selectedTeamId">
            @for (team of allTeamsList; track $index) {
            <mat-option [value]="team.id" (click)="teamSelected(team.id)">
              <span class="capitalize">{{team.title.rendered}}</span>
            </mat-option>
            } @empty {
            <mat-option disabled>No teams available</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <button type="button" class="bulk-select-all" (click)="toggleSelectAll()"
          [disabled]="displayedItems.length === 0">
          {{ selectAllChecked ? 'Deselect all' : 'Select all' }}
        </button>
      </div>

      <div *ngIf="getSelectedCount() > 0" class="bulk-chips">
        <div *ngFor="let userId of Array.from(selectedEntities)" class="bulk-chip">
          <ng-container *ngIf="getSelectedUserByIds(userId) as user">
            <img [src]="user.profile_image_url" class="bulk-chip-avatar"
              *ngIf="user.profile_image_url && user.profile_image_url !== null" alt="" />
            <ngx-avatars *ngIf="!user.profile_image_url || user.profile_image_url === null" name="{{ user.name }}"
              size="22" initialsSize="2"></ngx-avatars>
            <span>{{ user.name || userId }}</span>
          </ng-container>
          <button type="button" class="bulk-chip-remove" (click)="removeSelectedUser(userId)" aria-label="Remove">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
      </div>

      <div *ngIf="isLoading" class="bulk-loading">
        <mat-spinner diameter="28"></mat-spinner>
        <p>Loading {{ entityType }}s…</p>
      </div>

      <div class="bulk-grid" *ngIf="!isLoading">
        <ng-container *ngIf="isLoadingEmployees">
          <div *ngFor="let item of [1,2,3,4]" class="bulk-entity bulk-entity--skeleton">
            <span class="bulk-check" aria-hidden="true"></span>
            <div class="bulk-entity-avatar">
              <ngx-skeleton-loader [theme]="{ 'border-radius': '9999px', height: '36px', width: '36px'}"></ngx-skeleton-loader>
            </div>
            <div class="bulk-entity-meta">
              <ngx-skeleton-loader [theme]="{ 'border-radius': '4px', height: '12px', width: '70%' }"></ngx-skeleton-loader>
              <ngx-skeleton-loader [theme]="{ 'border-radius': '4px', height: '10px', width: '45%' }"></ngx-skeleton-loader>
            </div>
          </div>
        </ng-container>

        <ng-container *ngIf="!isLoadingEmployees && displayedItems.length > 0">
          <button type="button" *ngFor="let entity of displayedItems" (click)="toggleSelection(entity.id)"
            class="bulk-entity"
            [class.is-selected]="isSelected(entity.id)"
            [attr.aria-pressed]="isSelected(entity.id)">
            <span class="bulk-check" aria-hidden="true">
              <span class="material-symbols-outlined" *ngIf="isSelected(entity.id)">check</span>
            </span>
            <div class="bulk-entity-avatar">
              <img [src]="entity.profile_image_url"
                *ngIf="entity.profile_image_url && entity.profile_image_url !== 'null' && entity.profile_image_url !== null"
                alt="" />
              <ngx-avatars
                *ngIf="!entity.profile_image_url || entity.profile_image_url === 'null' || entity.profile_image_url === null"
                name="{{ entity.name }}" size="36" initialsSize="2"></ngx-avatars>
            </div>
            <div class="bulk-entity-meta">
              <strong>{{ entity.name }}</strong>
              <span *ngIf="entity.role">{{ entityType === 'employee' ? (entity.role | getUserRole) : entity.role }}</span>
            </div>
          </button>
        </ng-container>

        <div class="bulk-empty" *ngIf="!isLoadingEmployees && displayedItems.length === 0">
          <p>No {{ entityType }}s found</p>
        </div>
      </div>
    </section>

    <!-- Step 2 -->
    <section class="bulk-step">
      <header class="bulk-step-header">
        <div class="bulk-step-title">
          <span class="bulk-step-num" aria-hidden="true">2</span>
          <div>
            <h3>Configure settings</h3>
            <p>What to apply to the selection</p>
          </div>
        </div>
        <span class="bulk-status" [class.is-dirty]="isDirty">{{ isDirty ? 'Modified' : 'No changes yet' }}</span>
      </header>

      <form *ngIf="settingsForm" [formGroup]="settingsForm" class="bulk-config">
        <div class="bulk-config-block">
          <div class="bulk-config-head">
            <h4>Capture</h4>
            <p>{{ planScreenshotRetentionLabel() }}</p>
          </div>
          <div class="bulk-config-list">
            <div class="bulk-row">
              <div class="bulk-row-copy">
                <strong matTooltip="Timed screenshots for steady work tracking" matTooltipPosition="right">Periodic screenshots</strong>
              </div>
              <mat-slide-toggle color="primary" formControlName="ss_periodic"></mat-slide-toggle>
            </div>

            <div class="bulk-row bulk-row--nested" *ngIf="settingsForm.get('ss_periodic')?.value">
              <div class="bulk-row-copy">
                <strong>Interval <span class="settings-field-badge">{{ screenshotIntervalBadgeLabel() }}</span></strong>
                <span>Suggested 30 min</span>
                <span class="bulk-hint-inline" *ngIf="screenshotIntervalPlanHint()">{{ screenshotIntervalPlanHint() }}</span>
              </div>
              <app-minute-stepper formControlName="ss_screen_capture_time"
                [min]="minScreenshotCaptureMinutes()"></app-minute-stepper>
            </div>

            <div class="bulk-row" [class.is-muted]="!canAdvancedScreenshotModes()">
              <div class="bulk-row-copy">
                <strong matTooltip="Capture when someone switches apps" matTooltipPosition="right">App-switch screenshots
                  <span class="settings-field-badge">{{ advancedModesBadgeLabel() }}</span>
                </strong>
                <span *ngIf="!canAdvancedScreenshotModes()">{{ advancedScreenshotModesUpgradeMessage() }}</span>
              </div>
              <mat-slide-toggle color="primary" formControlName="ss_appchange"></mat-slide-toggle>
            </div>

            <div class="bulk-row bulk-row--nested" *ngIf="settingsForm.get('ss_appchange')?.value">
              <div class="bulk-row-copy">
                <strong>Delay after switch</strong>
                <span>Suggested 5 min</span>
              </div>
              <app-minute-stepper formControlName="ss_appchange_interval"></app-minute-stepper>
            </div>

            <div class="bulk-row">
              <div class="bulk-row-copy">
                <strong>Idle timeout</strong>
                <span>Suggested 5 min</span>
              </div>
              <app-minute-stepper formControlName="ss_idle_time"></app-minute-stepper>
            </div>

            <div class="bulk-row" [class.is-muted]="!isWebcamCaptureEntitled">
              <div class="bulk-row-copy">
                <strong matTooltip="Optional webcam snapshots" matTooltipPosition="right">Webcam photos
                  <span class="settings-field-badge">{{ webcamBadgeLabel() }}</span>
                </strong>
              </div>
              <mat-slide-toggle *ngIf="isWebcamCaptureEntitled" color="primary"
                formControlName="ss_webcam"></mat-slide-toggle>
              <mat-slide-toggle *ngIf="!isWebcamCaptureEntitled" color="primary" [checked]="isWebcamEnabled()"
                [disabled]="true"></mat-slide-toggle>
            </div>
          </div>
        </div>

        <div class="bulk-config-block" *ngIf="canAdvancedTrackingControls()">
          <div class="bulk-config-head">
            <h4>Tracking</h4>
            <p>How the agent behaves for the selection</p>
          </div>
          <div class="bulk-config-list">
            <div class="bulk-row">
              <div class="bulk-row-copy">
                <strong matTooltip="Background tracking with limited employee controls" matTooltipPosition="right">Silent mode</strong>
              </div>
              <mat-slide-toggle color="primary" (change)="silentModeToggleChange($event)"
                formControlName="ss_silent"></mat-slide-toggle>
            </div>
            <div class="bulk-row">
              <div class="bulk-row-copy">
                <strong matTooltip="Sign out if system time or timezone changes" matTooltipPosition="right">Strict time tracking</strong>
              </div>
              <mat-slide-toggle color="primary" formControlName="ss_strict_time_tracking"></mat-slide-toggle>
            </div>
            <div class="bulk-row">
              <div class="bulk-row-copy">
                <strong matTooltip="Starts automatically; employees can’t start or stop" matTooltipPosition="right">Auto tracking</strong>
                <span>Employees can’t start or stop the timer</span>
              </div>
              <mat-slide-toggle color="primary" formControlName="ss_automatic_tracking"></mat-slide-toggle>
            </div>
          </div>
        </div>
      </form>

    </section>
  </div>
</div>
